import React, { useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { GoogleGenerativeAI } from "@google/generative-ai"; // Ensure this package is installed

const ChatAi = () => {
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);

    // Function to handle sending a message
    const sendMessage = async () => {
        if (!input.trim()) return;

        const userMessage = {
            id: Date.now(),
            text: input,
            sender: "user",
        };
        setMessages((prevMessages) => [...prevMessages, userMessage]);
        setInput("");
        setLoading(true);

        try {
            // Instantiate the AI client (protect your API key in real apps)
            const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
            const model = genAI.getGenerativeModel({ model: "gemini-1.5-flash" });

            // Use the user's input as the prompt
            const result = await model.generateContent(userMessage.text + 'תענה אך ורק לשאלות של תזונה וספורט זה התחום אחריות שלך למענה לשאלה או שיחה,בתור יועץ תזונה ומאמן ');
            const aiText = result.response.text();

            const aiMessage = {
                id: Date.now() + 1,
                text: aiText,
                sender: "ai",
            };
            setMessages((prevMessages) => [...prevMessages, aiMessage]);
        } catch (error) {
            console.error("Error generating content:", error);
            // Optionally add error messaging here
        }
        setLoading(false);
    };

    // Handle the Enter key to send messages
    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            sendMessage();
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                // Responsive maxWidth: xs, sm, md, lg, and xl
                maxWidth: { xs: "70%", sm: "70%", md: "70%", lg: "70%", xl: "50%" },
                height: "70vh",
                margin: "20px auto",
                border: "1px solid #B5A78A",
                borderRadius: "12px",
                backgroundColor: "#ffffff",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
                fontFamily: '"Roboto", sans-serif',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    background: "linear-gradient(135deg, #B5A78A, rgba(156, 156, 116, 0.9))",
                    color: "#fff",
                    padding: "20px",
                    textAlign: "center",
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    boxShadow: "0px 4px 10px rgba(0,0,0,0.3)",
                    borderRadius: "8px",
                }}
            >
                <Typography variant="h5">💬 AI התייעץ עם</Typography>
            </Box>

            {/* Chat History */}
            <Box
                sx={{
                    flex: 1,
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    overflowY: "auto",
                    backgroundColor: "#f7f7f7",
                    // Scrollbar styling
                    "&::-webkit-scrollbar": { width: "8px" },
                    "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
                    "&::-webkit-scrollbar-thumb": { background: "#c1c1c1", borderRadius: "4px" },
                    // Center content if there are no messages
                    justifyContent: messages.length === 0 ? "center" : "flex-start",
                    alignItems: messages.length === 0 ? "center" : "stretch",
                }}
            >
                {messages.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: "center",
                            width: "100%",
                            fontSize: "2rem",
                            color: "#180a0a",
                            justifyContent: "center",
                        }}
                    >
                        ,שלום לך
                        <br /> ? במה אוכל לעזור היום
                    </Box>
                ) : (
                    messages.map((msg) => {
                        const isHebrew = /[\u0590-\u05FF]/.test(msg.text);
                        return (
                            <Box
                                key={msg.id}
                                sx={{
                                    padding: "12px 16px",
                                    borderRadius: "20px",
                                    maxWidth: "75%",
                                    lineHeight: 1.4,
                                    wordWrap: "break-word",
                                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                                    alignSelf: msg.sender === "user" ? "flex-end" : "flex-start",
                                    backgroundColor: msg.sender === "user" ? "#e8f0fe" : "#ffffff",
                                    border: msg.sender === "ai" ? "1px solid #dadce0" : "none",
                                    color: "#202124",
                                }}
                                dir={isHebrew ? "rtl" : "ltr"}
                            >
                                {msg.text}
                            </Box>
                        );
                    })
                )}
                {loading && (
                    <Box
                        sx={{
                            padding: "12px 16px",
                            borderRadius: "20px",
                            maxWidth: "75%",
                            lineHeight: 1.4,
                            wordWrap: "break-word",
                            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                            alignSelf: "flex-start",
                            backgroundColor: "#ffffff",
                            border: "1px solid #dadce0",
                            color: "#202124",
                        }}
                    >
                        {messages.length > 0 &&
                            /[\u0590-\u05FF]/.test(messages[messages.length - 1].text)
                            ? "...מקליד"
                            : "Typing..."}
                    </Box>
                )}
            </Box>

            {/* Input Area */}
            <Box
                sx={{
                    display: "flex",
                    padding: "12px",
                    backgroundColor: "#fff",
                    borderTop: "1px solid #e0e0e0",
                }}
            >
                <Button
                    onClick={sendMessage}
                    sx={{
                        marginLeft: "10px",
                        padding: "0 20px",
                        backgroundColor: "#B5A78A",
                        border: "none",
                        borderRadius: "20px",
                        color: "#fff",
                        fontSize: "1rem",
                        cursor: "pointer",
                        transition: "background-color 0.3s",
                        "&:hover": {
                            backgroundColor: "#B5A78A",
                        },
                    }}
                >
                    תשלח
                </Button>
                <TextField
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="תרשום כאן..."
                    variant="outlined"
                    InputProps={{
                        // Apply the input field styling directly to the inner <input> element
                        style: {
                            flex: 1,
                            padding: "10px 14px",
                            border: "1px solid #dadce0",
                            borderRadius: "20px",
                            outline: "none",
                            fontSize: "1rem",
                        },
                        // Set text direction based on content (RTL for Hebrew)
                        dir: input.length === 0 || /[\u0590-\u05FF]/.test(input) ? "rtl" : "ltr",
                    }}
                    sx={{
                        flex: 1,
                        marginLeft: "10px",
                        backgroundColor: "#fff",
                        // Remove the default outlined border so our inner style shows
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

export default ChatAi;
